import { useCallback, useEffect, useMemo, useState } from 'react';
import type { StatementResponse, StoreResponse } from '@clarity/contract';
import { useStatements } from '../../queries/index.js';
import { useScopeContext } from '../../lib/scopeContext.js';
import { useI18n } from '../../lib/i18n.js';
import { isOnboardingRequired, money } from '../format.js';
import { StatementsView } from '../store/shared.js';
import { StoreStatements } from '../store/StoreStatements.js';
import { OnboardingNote, splitOnboarded } from './shared.js';

/**
 * CombinedStatements (clarity-navigation-scope task 6.2, Req 6.1–6.4, design §7)
 * — the Statements flexible surface at an All / Brand scope spanning MORE than
 * one branch. It presents a **section per in-scope onboarded branch**, each
 * reusing the UNCHANGED single-store {@link StatementsView} (its month blocks +
 * per-period reconciliation verdicts), with a scope-level **roll-up** across
 * branches where a total is meaningful (total gross + total net paid).
 *
 * React-safe fan-out (same pattern as {@link CombinedOrders}): a hook can't be
 * called in a loop whose length varies across renders, so each branch gets its
 * own child — the visible section ({@link BranchStatementsSection}, which embeds
 * `StatementsView`) and a hidden source ({@link BranchStatementSource}) that
 * calls `useStatements(branch.id)` and REPORTS its totals up for the roll-up.
 * Both call the SAME per-branch query key, so React Query dedupes — the source
 * and the section share one cached read, no double fetch. Per-branch
 * loading/error stays ISOLATED inside each section (Req 6.3): one branch failing
 * only errors its own section; the rest render.
 *
 * These are cheap per-branch DB reads (not portal fetches; design §7 / Req 6.5),
 * sharing the `queryKeys.statements` cache with the single-store view.
 *
 * Single branch (Req 6.4): when the scope resolves to exactly one branch, it
 * renders the standalone single-store Statements page ({@link StoreStatements})
 * — its own page, no tab host. (Orders / Payouts are separate standalone pages
 * now; `?order=`/`?payout=` and the order→payout trace live on those pages.) No
 * "all" chrome.
 */

/** A shared frozen empty-rows reference, so the no-data case is stable across renders. */
const EMPTY_ROWS: readonly StatementResponse[] = Object.freeze([]);

/** The per-branch read state a {@link BranchStatementSource} reports up for the roll-up. */
interface BranchResult {
  isLoading: boolean;
  /** A genuine transport failure (NOT the expected `409 onboarding_required`). */
  isError: boolean;
  rows: readonly StatementResponse[];
}

export function CombinedStatements() {
  const { scope, branches, isSingleBranch } = useScopeContext();

  // Req 6.4 — a one-branch scope renders the standalone single-store Statements
  // page (a one-branch brand degenerates to its single branch; a store scope is
  // always one branch).
  if (isSingleBranch && branches.length === 1) {
    return (
      <section data-surface="statements" data-scope={scope.kind}>
        <StoreStatements store={branches[0] as StoreResponse} />
      </section>
    );
  }

  return <CombinedStatementsBody />;
}

/**
 * The multi-branch combined body. Split from {@link CombinedStatements} so the
 * single-branch passthrough never mounts the fan-out machinery and the hook
 * count stays stable for a given in-scope branch set.
 */
function CombinedStatementsBody() {
  const { scope, branches } = useScopeContext();

  // Only ONBOARDED branches contribute data reads (Req 6.2); the rest are counted.
  const { onboarded, stillOnboarding } = useMemo(
    () => splitOnboarded(branches),
    [branches],
  );
  const { t } = useI18n();

  // Per-branch totals, keyed by branch id, collected from the hidden sources for
  // the scope-level roll-up. The merge reads whatever has arrived so far, so a
  // slow/failed branch never blocks the roll-up (Req 6.3).
  const [results, setResults] = useState<Record<string, BranchResult>>({});

  const report = useCallback((branchId: string, result: BranchResult) => {
    setResults((prev) => {
      const existing = prev[branchId];
      if (
        existing &&
        existing.isLoading === result.isLoading &&
        existing.isError === result.isError &&
        existing.rows === result.rows
      ) {
        return prev;
      }
      return { ...prev, [branchId]: result };
    });
  }, []);

  // The scope-level roll-up (Req 6.1): Σ gross + Σ net paid across every
  // onboarded branch's statements. A total is meaningful here (both are
  // additive platform-currency figures), so we show it above the sections. Only
  // branches whose read has landed contribute; a still-loading / errored branch
  // is simply absent from the sum until it arrives.
  const rollup = useMemo(() => {
    let totalGross = 0;
    let totalNetPayout = 0;
    let periods = 0;
    for (const branch of onboarded) {
      const result = results[branch.id];
      if (!result) continue;
      for (const s of result.rows) {
        totalGross += s.totalGross;
        totalNetPayout += s.totalNetPayout;
        periods += 1;
      }
    }
    return { totalGross, totalNetPayout, periods };
  }, [onboarded, results]);

  const scopeNoBranches = onboarded.length === 0;

  return (
    <section data-surface="statements" data-scope={scope.kind} className="combined-view">
      {/* The fan-out sources: one hidden read per onboarded branch, feeding the
          roll-up. The visible sections below call the same query key, so these
          add no extra network — only the roll-up subscription. */}
      {onboarded.map((branch) => (
        <BranchStatementSource key={branch.id} branch={branch} onResult={report} />
      ))}

      {stillOnboarding > 0 && <OnboardingNote count={stillOnboarding} t={t} />}

      {scopeNoBranches ? (
        <p className="muted">
          {stillOnboarding > 0
            ? 'All in-scope branches are still onboarding — no statements to combine yet.'
            : 'No branches in scope.'}
        </p>
      ) : (
        <>
          {rollup.periods > 0 && (
            <table
              className="data-table combined-view__rollup"
              aria-label="Statements roll-up"
            >
              <tbody>
                <tr>
                  <th scope="row">Total gross</th>
                  <td>{money(rollup.totalGross)}</td>
                </tr>
                <tr>
                  <th scope="row">Total net paid</th>
                  <td>{money(rollup.totalNetPayout)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {onboarded.map((branch) => (
            <BranchStatementsSection key={branch.id} branch={branch} />
          ))}
        </>
      )}
    </section>
  );
}

/**
 * One visible per-branch section: a branch heading + the UNCHANGED single-store
 * {@link StatementsView} (Req 6.4 reuse). `StatementsView` owns its own
 * loading/error/empty handling, so a branch that fails or is still loading shows
 * that inside its own section — isolated from the other sections (Req 6.3).
 */
function BranchStatementsSection({ branch }: { branch: StoreResponse }) {
  return (
    <section className="combined-view__branch" aria-label={branch.name ?? branch.externalStoreId}>
      <h3 className="combined-view__branch-head">
        <span className="combined-view__store">{branch.name ?? branch.externalStoreId}</span>
        <span className="cell-sub"> {branch.externalStoreId}</span>
      </h3>
      <StatementsView store={branch} />
    </section>
  );
}

/**
 * One hidden per-branch read source feeding the roll-up (design §7). It calls
 * the existing {@link useStatements} hook for ONE branch and reports its totals
 * up via `onResult`; it renders nothing. The expected `409 onboarding_required`
 * is NOT treated as a failure (defense-in-depth for a just-flipped lifecycle):
 * it reports empty rows and no error.
 */
function BranchStatementSource({
  branch,
  onResult,
}: {
  branch: StoreResponse;
  onResult: (branchId: string, result: BranchResult) => void;
}) {
  const statements = useStatements(branch.id);
  const onboardingBlocked = isOnboardingRequired(statements.error);
  const isError = statements.isError && !onboardingBlocked;
  const { isLoading } = statements;
  const rows: readonly StatementResponse[] =
    statements.data?.status === 200 && !onboardingBlocked ? statements.data.body : EMPTY_ROWS;

  // Report the latest state up in an EFFECT, not during render (a child must not
  // setState a parent mid-render). `onResult` is memoized and dedupes no-ops.
  useEffect(() => {
    onResult(branch.id, { isLoading, isError, rows });
  }, [branch.id, isLoading, isError, rows, onResult]);

  return null;
}
